import { useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';
import type { FilterField } from './filters/filterUtils';
import {
  buildFilterParsers,
  buildGraphQLFilter,
  clearPayload,
  hasActiveFilters,
  normalizeFilters,
} from './filters/filterUtils';

/**
 * All available machine filter fields.
 * Add new filters here to make them available throughout the app.
 * The graphKey maps to the backend GraphQL filter field name.
 */
export interface MachineFilters {
  // Search filters
  displayName: string;
  serial: string;
  partNumber: string;
  soid: string;
  machineIdFilter: string;
  zipCode: string;
  address1: string;
  edgeDeviceId: string;
  edgeDeviceGuid: string;
  edgeDeviceName: string;
  // Multi-select filters
  customers: string[];
  plants: string[];
  families: string[];
  statuses: string[];
  countryCodes: string[];
  countries: string[];
  cities: string[];
  states: string[];
  edgeDeviceStatuses: string[];
  edgeDeviceConnectionStatuses: string[];
}

/**
 * Edge device provisioning states. EDMS returns no edge device at all for a
 * machine that was never provisioned - these values make that explicit and
 * filterable rather than leaving it to be inferred from a blank id.
 */
export const EDGE_DEVICE_STATUS_OPTIONS = [
  { value: 'provisioned', label: 'Provisioned' },
  { value: 'not_provisioned', label: 'Not provisioned' },
];

/**
 * Edge device IoT hub connectivity, as EDMS device management reports it. This
 * is not the machine's state: a device can be Online while nothing on it
 * publishes machine state, which reads as an offline machine.
 */
export const EDGE_DEVICE_CONNECTION_STATUS_OPTIONS = [
  { value: 'Online', label: 'Online' },
  { value: 'Offline', label: 'Offline' },
  { value: 'Unknown', label: 'Unknown' },
  { value: 'No device', label: 'No device' },
];

/**
 * Filter field configuration - maps frontend filter keys to GraphQL filter keys.
 * This is the single source of truth for filter configuration.
 */
const fields = [
  // Search filters
  { key: 'displayName', type: 'search', graphKey: 'displayName' },
  { key: 'serial', type: 'search', graphKey: 'serial' },
  { key: 'partNumber', type: 'search', graphKey: 'partNumber' },
  { key: 'soid', type: 'search', graphKey: 'soid' },
  { key: 'machineIdFilter', type: 'search', graphKey: 'machineIdFilter' },
  { key: 'zipCode', type: 'search', graphKey: 'zipCode' },
  { key: 'address1', type: 'search', graphKey: 'address1' },
  { key: 'edgeDeviceId', type: 'search', graphKey: 'edgeDeviceId' },
  { key: 'edgeDeviceGuid', type: 'search', graphKey: 'edgeDeviceGuid' },
  { key: 'edgeDeviceName', type: 'search', graphKey: 'edgeDeviceName' },
  // Multi-select filters
  { key: 'customers', type: 'multi', graphKey: 'customerIds' },
  { key: 'plants', type: 'multi', graphKey: 'plantIds' },
  { key: 'families', type: 'multi', graphKey: 'familyIds' },
  { key: 'statuses', type: 'multi', graphKey: 'statuses' },
  { key: 'countryCodes', type: 'multi', graphKey: 'countryCodes' },
  { key: 'countries', type: 'multi', graphKey: 'countries' },
  { key: 'cities', type: 'multi', graphKey: 'cities' },
  { key: 'states', type: 'multi', graphKey: 'states' },
  {
    key: 'edgeDeviceStatuses',
    type: 'multi',
    graphKey: 'edgeDeviceStatuses',
  },
  {
    key: 'edgeDeviceConnectionStatuses',
    type: 'multi',
    graphKey: 'edgeDeviceConnectionStatuses',
  },
] satisfies Array<FilterField<keyof MachineFilters>>;

const filterParsers = buildFilterParsers<keyof MachineFilters>(
  fields
) as Parameters<typeof useQueryStates>[0];

/**
 * Hook to manage machine filters via URL query params.
 * This is the single source of truth for machine filter state.
 * No prop drilling needed - use this hook anywhere you need machine filters.
 */
export function useMachineFilters() {
  const [filters, setUrlState] = useQueryStates(filterParsers);

  const normalizedFilters = useMemo(
    () =>
      normalizeFilters<MachineFilters, keyof MachineFilters>(filters, fields),
    [filters]
  );

  /**
   * Generic setter for any filter field
   */
  const setFilter = useCallback(
    <K extends keyof MachineFilters>(key: K, value: MachineFilters[K]) => {
      const field = fields.find((f) => f.key === key);
      if (!field) return;

      if (field.type === 'search') {
        setUrlState({ [key]: (value as string) || null });
      } else {
        const arr = value as string[];
        setUrlState({ [key]: arr.length > 0 ? arr : null });
      }
    },
    [setUrlState]
  );

  // Convenience setters for common filters
  const setDisplayName = useCallback(
    (value: string) => setFilter('displayName', value),
    [setFilter]
  );

  const setSerial = useCallback(
    (value: string) => setFilter('serial', value),
    [setFilter]
  );

  const setPartNumber = useCallback(
    (value: string) => setFilter('partNumber', value),
    [setFilter]
  );

  const setCustomers = useCallback(
    (values: string[]) => setFilter('customers', values),
    [setFilter]
  );

  const setPlants = useCallback(
    (values: string[]) => setFilter('plants', values),
    [setFilter]
  );

  const setFamilies = useCallback(
    (values: string[]) => setFilter('families', values),
    [setFilter]
  );

  const setStatuses = useCallback(
    (values: string[]) => setFilter('statuses', values),
    [setFilter]
  );

  const setCountryCodes = useCallback(
    (values: string[]) => setFilter('countryCodes', values),
    [setFilter]
  );

  const setCountries = useCallback(
    (values: string[]) => setFilter('countries', values),
    [setFilter]
  );

  const setCities = useCallback(
    (values: string[]) => setFilter('cities', values),
    [setFilter]
  );

  const setStates = useCallback(
    (values: string[]) => setFilter('states', values),
    [setFilter]
  );

  const setFilters = useCallback(
    (newFilters: Partial<MachineFilters>) => {
      const updates: Record<string, string | string[] | null> = {};

      for (const field of fields) {
        const key = field.key as keyof MachineFilters;
        if (newFilters[key] !== undefined) {
          if (field.type === 'search') {
            updates[key] = (newFilters[key] as string) || null;
          } else {
            const arr = newFilters[key] as string[];
            updates[key] = arr.length > 0 ? arr : null;
          }
        }
      }

      setUrlState(updates);
    },
    [setUrlState]
  );

  const clearFilters = useCallback(() => {
    setUrlState(clearPayload(fields));
  }, [setUrlState]);

  const active = useMemo(() => hasActiveFilters(filters, fields), [filters]);

  const graphQLFilter = useMemo(
    () => buildGraphQLFilter(filters, fields),
    [filters]
  );

  return {
    filters: normalizedFilters,
    // Generic setter
    setFilter,
    // Convenience setters
    setDisplayName,
    setSerial,
    setPartNumber,
    setCustomers,
    setPlants,
    setFamilies,
    setStatuses,
    setCountryCodes,
    setCountries,
    setCities,
    setStates,
    // Batch operations
    setFilters,
    clearFilters,
    // State
    hasActiveFilters: active,
    graphQLFilter,
  };
}
